import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Image as ImageIcon, 
  Link2, 
  Building2, 
  MapPin, 
  Users, 
  Calendar,
  Sparkles,
  Target,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Save,
  Send,
  Loader2,
  X
} from 'lucide-react';

const DH_DESIGN_LOGO = "https://customer-assets.emergentagent.com/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/f6qq4dmm_1000022581.png";

const BRAND_ADJECTIVES = [
  "Luxury", "Modern", "Boutique", "Elevated Casual", "Corporate Refined",
  "Experiential", "Heritage", "Minimalist", "Destination-Driven"
];

const DEPARTMENTS = [
  "Front Desk", "Concierge", "F&B", "Housekeeping", 
  "Management", "Events", "Other"
];

const PROGRAM_TYPES = ["Replacement", "New Program", "Mixed"];

const RISK_EXAMPLES = [
  "Budget sensitivity",
  "Leadership alignment issues",
  "Competitive pricing pressure",
  "Timeline constraints",
  "Vendor loyalty",
  "Decision-maker hesitation"
];

export default function DeckIntake() {
  const { user } = useAuth();
  const { post, uploadFile, loading } = useApi();
  const navigate = useNavigate();
  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({
    // Sales Rep Email
    sales_rep_email: user?.email || '',
    
    // Section A - Supporting Materials
    pdfFiles: [],
    imageFiles: [],
    relevant_links: '',
    
    // Section B - Opportunity Snapshot
    account_name: '',
    property_location: '',
    estimated_team_size: '',
    target_launch_timeline: '',
    
    // Section C - Brand Adjectives
    brand_adjectives: [],
    
    // Section D - Competitive Reference
    competitive_reference: '',
    
    // Section E - Departments
    departments: [],
    other_department: '',
    
    // Section F - Program Type
    program_type: '',
    
    // Section G - Risks
    risks_complications: '',
    
    // Section H - Confirmation
    premium_criteria_confirmed: false
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [opportunityId, setOpportunityId] = useState(null);

  const sections = [
    { id: 'materials', title: 'Supporting Materials', icon: Upload, optional: true },
    { id: 'snapshot', title: 'Opportunity Snapshot', icon: Building2, optional: false },
    { id: 'perception', title: 'Brand Perception', icon: Sparkles, optional: false },
    { id: 'competitive', title: 'Competitive Reference', icon: Target, optional: true },
    { id: 'departments', title: 'Departments', icon: Briefcase, optional: false },
    { id: 'program', title: 'Program Type', icon: FileText, optional: false },
    { id: 'risks', title: 'Risks & Complications', icon: AlertTriangle, optional: false },
    { id: 'confirm', title: 'Confirmation', icon: CheckCircle, optional: false },
  ];

  const progress = ((currentSection + 1) / sections.length) * 100;

  const handleFileUpload = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'pdf') {
      setFormData(prev => ({ ...prev, pdfFiles: [...prev.pdfFiles, ...files] }));
    } else {
      setFormData(prev => ({ ...prev, imageFiles: [...prev.imageFiles, ...files] }));
    }
  };

  const removeFile = (type, index) => {
    if (type === 'pdf') {
      setFormData(prev => ({ 
        ...prev, 
        pdfFiles: prev.pdfFiles.filter((_, i) => i !== index) 
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        imageFiles: prev.imageFiles.filter((_, i) => i !== index) 
      }));
    }
  };

  const handleAdjectiveToggle = (adjective) => {
    setFormData(prev => {
      const current = prev.brand_adjectives;
      if (current.includes(adjective)) {
        return { ...prev, brand_adjectives: current.filter(a => a !== adjective) };
      } else if (current.length < 3) {
        return { ...prev, brand_adjectives: [...current, adjective] };
      }
      return prev;
    });
  };

  const handleDepartmentToggle = (dept) => {
    setFormData(prev => {
      const current = prev.departments;
      if (current.includes(dept)) {
        return { ...prev, departments: current.filter(d => d !== dept) };
      }
      return { ...prev, departments: [...current, dept] };
    });
  };

  const validateSection = (sectionIndex) => {
    const newErrors = {};
    
    switch (sectionIndex) {
      case 1: // Snapshot
        if (!formData.sales_rep_email) newErrors.sales_rep_email = 'Email address is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.sales_rep_email)) {
          newErrors.sales_rep_email = 'Please enter a valid email address';
        }
        if (!formData.account_name) newErrors.account_name = 'Account name is required';
        if (!formData.property_location) newErrors.property_location = 'Property location is required';
        if (!formData.estimated_team_size) newErrors.estimated_team_size = 'Team size is required';
        if (!formData.target_launch_timeline) newErrors.target_launch_timeline = 'Launch timeline is required';
        break;
      case 2: // Perception
        if (formData.brand_adjectives.length !== 3) {
          newErrors.brand_adjectives = 'Please select exactly 3 adjectives';
        }
        break;
      case 4: // Departments
        if (formData.departments.length === 0) {
          newErrors.departments = 'Please select at least one department';
        }
        if (formData.departments.includes('Other') && !formData.other_department) {
          newErrors.other_department = 'Please specify the other department';
        }
        break;
      case 5: // Program Type
        if (!formData.program_type) newErrors.program_type = 'Please select a program type';
        break;
      case 6: // Risks
        if (!formData.risks_complications) newErrors.risks_complications = 'Please describe potential risks';
        break;
      case 7: // Confirmation
        if (!formData.premium_criteria_confirmed) {
          newErrors.premium_criteria_confirmed = 'Please confirm premium criteria';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateSection(currentSection)) {
      setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentSection(prev => Math.max(prev - 1, 0));
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      const result = await post('/deck-intake', {
        sales_rep_email: formData.sales_rep_email || '',
        account_name: formData.account_name || 'Untitled Draft',
        property_location: formData.property_location || '',
        estimated_team_size: parseInt(formData.estimated_team_size) || 0,
        target_launch_timeline: formData.target_launch_timeline || '',
        brand_adjectives: formData.brand_adjectives,
        competitive_reference: formData.competitive_reference,
        departments: formData.departments,
        other_department: formData.other_department,
        program_type: formData.program_type || 'New Program',
        risks_complications: formData.risks_complications || '',
        premium_criteria_confirmed: formData.premium_criteria_confirmed,
        relevant_links: formData.relevant_links,
        is_draft: true
      });
      
      toast.success('Draft saved successfully');
      setOpportunityId(result.opportunity_id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all required sections
    for (let i = 0; i < sections.length; i++) {
      if (!sections[i].optional && !validateSection(i)) {
        setCurrentSection(i);
        toast.error(`Please complete the ${sections[i].title} section`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const result = await post('/deck-intake', {
        sales_rep_email: formData.sales_rep_email,
        account_name: formData.account_name,
        property_location: formData.property_location,
        estimated_team_size: parseInt(formData.estimated_team_size),
        target_launch_timeline: formData.target_launch_timeline,
        brand_adjectives: formData.brand_adjectives,
        competitive_reference: formData.competitive_reference,
        departments: formData.departments,
        other_department: formData.other_department,
        program_type: formData.program_type,
        risks_complications: formData.risks_complications,
        premium_criteria_confirmed: formData.premium_criteria_confirmed,
        relevant_links: formData.relevant_links,
        is_draft: false
      });

      const oppId = result.opportunity_id;
      setOpportunityId(oppId);

      // Upload files if any
      for (const file of [...formData.pdfFiles, ...formData.imageFiles]) {
        try {
          await uploadFile(`/upload/${oppId}`, file);
        } catch (err) {
          console.error('File upload error:', err);
        }
      }

      setSubmitted(true);
      toast.success('Intake submitted successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 text-center animate-fade-in" data-testid="deck-intake-success">
          <img 
            src={DH_DESIGN_LOGO} 
            alt="DH Design" 
            className="h-16 mx-auto mb-8"
          />
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-semibold mb-4">Intake Received</h1>
          <p className="text-muted-foreground mb-2">
            Your Premium Vision Deck Intake for <strong>{formData.account_name}</strong> is in.
          </p>
          <p className="text-muted-foreground mb-8">
            Next up, we'll review the inputs, confirm positioning, and move into visual execution.
          </p>
          
          <Card className="rounded-sm border bg-muted/30 text-left mb-8">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-2">Confirmation email sent to:</p>
              <p className="font-medium">{user?.email}</p>
            </CardContent>
          </Card>

          <div className="flex gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/pipeline')} 
              className="rounded-sm"
            >
              View Pipeline
            </Button>
            <Button 
              onClick={() => navigate(`/opportunity/${opportunityId}`)} 
              className="rounded-sm"
              data-testid="view-opportunity-btn"
            >
              View Intake Details
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="deck-intake-page">
        {/* Header with DH Design Logo */}
        <div className="text-center pb-6 border-b border-border">
          <img 
            src={DH_DESIGN_LOGO} 
            alt="DH Design" 
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-2xl sm:text-3xl font-semibold mb-2">Premium Vision Deck Intake</h1>
          <p className="text-muted-foreground">
            Estimated time: 4-5 minutes
          </p>
          <p className="text-muted-foreground text-sm sm:text-base font-medium mt-2 tracking-wide">
            Strategic clarity in. Structured premium presentation delivered.
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Section {currentSection + 1} of {sections.length}: {sections[currentSection].title}
              {sections[currentSection].optional && <span className="text-muted-foreground/60 ml-1">(Optional)</span>}
            </span>
            <span className="text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1" />
        </div>

        {/* Section Navigation Pills */}
        <div className="flex flex-wrap gap-2 pb-4">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setCurrentSection(index)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                  currentSection === index
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
                data-testid={`section-nav-${section.id}`}
              >
                <Icon className="h-3 w-3" />
                {section.title}
              </button>
            );
          })}
        </div>

        {/* Form Sections */}
        <Card className="rounded-sm border">
          <CardContent className="p-6 sm:p-8">
            {/* Section A - Supporting Materials */}
            {currentSection === 0 && (
              <div className="space-y-6" data-testid="section-materials">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Upload PDF(s)</Label>
                    <p className="text-xs text-muted-foreground mb-3">Brand guides, presentations, or reference documents</p>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf"
                      multiple
                      onChange={(e) => handleFileUpload(e, 'pdf')}
                      className="hidden"
                      data-testid="pdf-upload-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => pdfInputRef.current?.click()}
                      className="rounded-sm w-full justify-center h-24 border-dashed"
                    >
                      <div className="text-center">
                        <FileText className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                        <span className="text-sm">Click to upload PDFs</span>
                      </div>
                    </Button>
                    {formData.pdfFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {formData.pdfFiles.map((file, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-muted rounded-sm">
                            <span className="text-sm truncate">{file.name}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeFile('pdf', i)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Upload Images</Label>
                    <p className="text-xs text-muted-foreground mb-3">Logos, photos, or visual references</p>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleFileUpload(e, 'image')}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      className="rounded-sm w-full justify-center h-24 border-dashed"
                    >
                      <div className="text-center">
                        <ImageIcon className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                        <span className="text-sm">Click to upload images</span>
                      </div>
                    </Button>
                    {formData.imageFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {formData.imageFiles.map((file, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-muted rounded-sm">
                            <span className="text-sm truncate">{file.name}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeFile('image', i)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Paste Relevant Links</Label>
                    <p className="text-xs text-muted-foreground mb-3">Website URLs, Dropbox links, etc.</p>
                    <Textarea
                      value={formData.relevant_links}
                      onChange={(e) => setFormData({ ...formData, relevant_links: e.target.value })}
                      placeholder="https://example.com&#10;https://dropbox.com/..."
                      rows={4}
                      className="rounded-sm resize-none"
                      data-testid="relevant-links-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section B - Opportunity Snapshot */}
            {currentSection === 1 && (
              <div className="space-y-6" data-testid="section-snapshot">
                <div className="space-y-4">
                  {/* Sales Rep Email Field */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Your Email Address <span className="text-destructive">*</span>
                    </Label>
                    <p className="text-xs text-muted-foreground">We'll send the completed design deck to this address</p>
                    <Input
                      type="email"
                      value={formData.sales_rep_email}
                      onChange={(e) => setFormData({ ...formData, sales_rep_email: e.target.value })}
                      placeholder="your.email@company.com"
                      className={`rounded-sm ${errors.sales_rep_email ? 'border-destructive' : ''}`}
                      data-testid="sales-rep-email-input"
                    />
                    {errors.sales_rep_email && (
                      <p className="text-xs text-destructive">{errors.sales_rep_email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Account Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      placeholder="e.g., The Ritz-Carlton"
                      className={`rounded-sm ${errors.account_name ? 'border-destructive' : ''}`}
                      data-testid="account-name-input"
                    />
                    {errors.account_name && (
                      <p className="text-xs text-destructive">{errors.account_name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Property Location <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={formData.property_location}
                      onChange={(e) => setFormData({ ...formData, property_location: e.target.value })}
                      placeholder="e.g., Miami Beach, FL"
                      className={`rounded-sm ${errors.property_location ? 'border-destructive' : ''}`}
                      data-testid="property-location-input"
                    />
                    {errors.property_location && (
                      <p className="text-xs text-destructive">{errors.property_location}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Estimated Team Size <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={formData.estimated_team_size}
                      onChange={(e) => setFormData({ ...formData, estimated_team_size: e.target.value })}
                      placeholder="e.g., 150"
                      className={`rounded-sm ${errors.estimated_team_size ? 'border-destructive' : ''}`}
                      data-testid="team-size-input"
                    />
                    {errors.estimated_team_size && (
                      <p className="text-xs text-destructive">{errors.estimated_team_size}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Target Launch Timeline <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={formData.target_launch_timeline}
                      onChange={(e) => setFormData({ ...formData, target_launch_timeline: e.target.value })}
                      placeholder="e.g., Q2 2026 or June 2026"
                      className={`rounded-sm ${errors.target_launch_timeline ? 'border-destructive' : ''}`}
                      data-testid="timeline-input"
                    />
                    {errors.target_launch_timeline && (
                      <p className="text-xs text-destructive">{errors.target_launch_timeline}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Section C - Brand Perception */}
            {currentSection === 2 && (
              <div className="space-y-6" data-testid="section-perception">
                <div>
                  <Label className="text-sm font-medium mb-1 block">
                    How must this property be perceived? <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Select exactly 3 brand adjectives ({formData.brand_adjectives.length}/3 selected)
                  </p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {BRAND_ADJECTIVES.map((adj) => {
                      const isSelected = formData.brand_adjectives.includes(adj);
                      const isDisabled = !isSelected && formData.brand_adjectives.length >= 3;
                      
                      return (
                        <button
                          key={adj}
                          type="button"
                          onClick={() => handleAdjectiveToggle(adj)}
                          disabled={isDisabled}
                          className={`p-3 rounded-sm border text-sm font-medium transition-all text-left ${
                            isSelected
                              ? 'bg-primary text-primary-foreground border-primary'
                              : isDisabled
                              ? 'bg-muted/30 text-muted-foreground/50 border-border cursor-not-allowed'
                              : 'bg-background hover:bg-muted border-border'
                          }`}
                          data-testid={`adjective-${adj.toLowerCase().replace(' ', '-')}`}
                        >
                          {adj}
                        </button>
                      );
                    })}
                  </div>
                  
                  {errors.brand_adjectives && (
                    <p className="text-xs text-destructive mt-2">{errors.brand_adjectives}</p>
                  )}
                </div>
              </div>
            )}

            {/* Section D - Competitive Reference */}
            {currentSection === 3 && (
              <div className="space-y-6" data-testid="section-competitive">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Competitive Reference</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Any competitors or similar properties we should be aware of?
                  </p>
                  <Textarea
                    value={formData.competitive_reference}
                    onChange={(e) => setFormData({ ...formData, competitive_reference: e.target.value })}
                    placeholder="e.g., Four Seasons Miami, Faena Hotel..."
                    rows={4}
                    className="rounded-sm resize-none"
                    data-testid="competitive-reference-input"
                  />
                </div>
              </div>
            )}

            {/* Section E - Departments */}
            {currentSection === 4 && (
              <div className="space-y-6" data-testid="section-departments">
                <div>
                  <Label className="text-sm font-medium mb-1 block">
                    Departments Included <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Select all departments that will be included in this program
                  </p>
                  
                  <div className="space-y-3">
                    {DEPARTMENTS.map((dept) => (
                      <div key={dept} className="flex items-center space-x-3">
                        <Checkbox
                          id={dept}
                          checked={formData.departments.includes(dept)}
                          onCheckedChange={() => handleDepartmentToggle(dept)}
                          className="rounded-sm"
                          data-testid={`dept-${dept.toLowerCase().replace(' ', '-')}`}
                        />
                        <Label htmlFor={dept} className="text-sm cursor-pointer">
                          {dept}
                        </Label>
                      </div>
                    ))}
                  </div>
                  
                  {formData.departments.includes('Other') && (
                    <div className="mt-4 space-y-2">
                      <Label className="text-sm font-medium">
                        Specify Other <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={formData.other_department}
                        onChange={(e) => setFormData({ ...formData, other_department: e.target.value })}
                        placeholder="e.g., Valet, Spa Services..."
                        className={`rounded-sm ${errors.other_department ? 'border-destructive' : ''}`}
                        data-testid="other-department-input"
                      />
                      {errors.other_department && (
                        <p className="text-xs text-destructive">{errors.other_department}</p>
                      )}
                    </div>
                  )}
                  
                  {errors.departments && (
                    <p className="text-xs text-destructive mt-2">{errors.departments}</p>
                  )}
                </div>
              </div>
            )}

            {/* Section F - Program Type */}
            {currentSection === 5 && (
              <div className="space-y-6" data-testid="section-program">
                <div>
                  <Label className="text-sm font-medium mb-1 block">
                    Program Type <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Is this a replacement, new program, or mixed?
                  </p>
                  
                  <RadioGroup
                    value={formData.program_type}
                    onValueChange={(v) => setFormData({ ...formData, program_type: v })}
                    className="space-y-3"
                  >
                    {PROGRAM_TYPES.map((type) => (
                      <div key={type} className="flex items-center space-x-3">
                        <RadioGroupItem 
                          value={type} 
                          id={type}
                          className="rounded-sm"
                          data-testid={`program-${type.toLowerCase().replace(' ', '-')}`}
                        />
                        <Label htmlFor={type} className="text-sm cursor-pointer">
                          {type}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  
                  {errors.program_type && (
                    <p className="text-xs text-destructive mt-2">{errors.program_type}</p>
                  )}
                </div>
              </div>
            )}

            {/* Section G - Risks */}
            {currentSection === 6 && (
              <div className="space-y-6" data-testid="section-risks">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    What could slow or complicate this opportunity? <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    Examples: {RISK_EXAMPLES.join(' • ')}
                  </p>
                  <Textarea
                    value={formData.risks_complications}
                    onChange={(e) => setFormData({ ...formData, risks_complications: e.target.value })}
                    placeholder="Describe any potential risks, complications, or concerns..."
                    rows={6}
                    className={`rounded-sm resize-none ${errors.risks_complications ? 'border-destructive' : ''}`}
                    data-testid="risks-input"
                  />
                  {errors.risks_complications && (
                    <p className="text-xs text-destructive">{errors.risks_complications}</p>
                  )}
                </div>
              </div>
            )}

            {/* Section H - Confirmation */}
            {currentSection === 7 && (
              <div className="space-y-6" data-testid="section-confirm">
                <div className="p-6 bg-muted/30 rounded-sm border">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="confirm"
                      checked={formData.premium_criteria_confirmed}
                      onCheckedChange={(checked) => setFormData({ ...formData, premium_criteria_confirmed: checked })}
                      className="rounded-sm mt-0.5"
                      data-testid="premium-confirm-checkbox"
                    />
                    <Label htmlFor="confirm" className="text-sm cursor-pointer leading-relaxed">
                      I confirm this opportunity meets premium positioning criteria.
                    </Label>
                  </div>
                  {errors.premium_criteria_confirmed && (
                    <p className="text-xs text-destructive mt-2">{errors.premium_criteria_confirmed}</p>
                  )}
                </div>

                {/* Summary */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm">Submission Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Account</p>
                      <p className="font-medium">{formData.account_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Location</p>
                      <p className="font-medium">{formData.property_location || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Team Size</p>
                      <p className="font-medium">{formData.estimated_team_size || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Timeline</p>
                      <p className="font-medium">{formData.target_launch_timeline || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Brand Perception</p>
                      <p className="font-medium">{formData.brand_adjectives.join(', ') || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Departments</p>
                      <p className="font-medium">{formData.departments.join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Program Type</p>
                      <p className="font-medium">{formData.program_type || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Files</p>
                      <p className="font-medium">
                        {formData.pdfFiles.length + formData.imageFiles.length} attached
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons - with mobile safe area padding */}
        <div className="flex justify-between items-center pt-4 pb-safe mb-4">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrev}
            disabled={currentSection === 0}
            className="rounded-sm"
            data-testid="prev-btn"
          >
            Previous
          </Button>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveDraft}
              disabled={submitting}
              className="rounded-sm"
              data-testid="save-draft-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Draft
            </Button>

            {currentSection < sections.length - 1 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="rounded-sm"
                data-testid="next-btn"
              >
                Next
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !formData.premium_criteria_confirmed}
                className="rounded-sm"
                data-testid="submit-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Submit Intake
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
